/**
 * The front door to the phase-suggestion review pages — pick a student, open
 * their suggestion.
 *
 * A list of names. The one thing that distinguishes a card is how much the
 * coach's notes gave the designer to work with, so that is the only thing a
 * card carries besides the name. Everything else the manifest knows —
 * generation date, manual version, exercise counts, phase history — is on the
 * page it opens, where it has a use.
 *
 * Cards are ordinary links. Opening one is the same navigation as pasting the
 * URL: this page reads the manifest and writes nothing.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Me } from "../../auth";
import type { Annotation, Manifest, ManifestEntry } from "./types";
import "./phase-review.css";

/** Richest first: considerations, then notes behind them, then name. */
function byDepth(a: ManifestEntry, b: ManifestEntry): number {
  if (a.considerations !== b.considerations) return b.considerations - a.considerations;
  if ((b.notes || 0) !== (a.notes || 0)) return (b.notes || 0) - (a.notes || 0);
  return a.name.localeCompare(b.name);
}

/* ============================================================
 *  this reviewer's own mark counts, one call per sample
 * ============================================================
 * The annotations route returns every author's marks for a sample; the count
 * on a card is only ever this reviewer's own — it is where they left off. A
 * failed count is dropped rather than surfaced.
 */
function useMyMarkCounts(
  instance: string, samples: ManifestEntry[], email: string,
): Record<string, number> {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const ids = samples.map(s => s.id).join(",");

  useEffect(() => {
    if (!ids || !email) return;
    let alive = true;
    Promise.all(ids.split(",").map(async id => {
      try {
        const r = await fetch(
          `/api/${instance}/phase-review/annotations?sample=${encodeURIComponent(id)}`,
        );
        if (!r.ok) return [id, 0] as const;
        const d = await r.json() as { annotations: Annotation[] };
        return [id, (d.annotations || []).filter(a => a.author_email === email).length] as const;
      } catch {
        return [id, 0] as const;
      }
    })).then(pairs => {
      if (alive) setCounts(Object.fromEntries(pairs));
    });
    return () => { alive = false; };
  }, [instance, ids, email]);

  return counts;
}

/* ============================================================
 *  generation
 * ============================================================
 * The poll is what advances a run, and the server knows which runs are in
 * flight — so the page asks it rather than remembering. A refresh mid-run picks
 * the run back up instead of stranding it.
 */

interface Run {
  runId: string;
  status: "running" | "done" | "error";
  student: string;
  stageLabel?: string;
  considerations?: number;
  error?: string;
}

const POLL_MS = 5000;

function useRuns(instance: string, onSettled: () => void) {
  const [runs, setRuns] = useState<Run[]>([]);
  const [failures, setFailures] = useState<Run[]>([]);
  const timer = useRef<number | undefined>(undefined);
  const seen = useRef<Set<string>>(new Set());

  const sweep = useCallback(async () => {
    try {
      const r = await fetch(`/api/${instance}/phase-review/generate`);
      if (r.ok) {
        const d = await r.json() as { runs: Run[] };
        const all = d.runs || [];
        const live = all.filter(x => x.status === "running");
        // A run that has just left the list finished or died; either way the
        // page below it is now stale.
        for (const x of all) {
          if (x.status !== "running" && !seen.current.has(x.runId)) {
            seen.current.add(x.runId);
            if (x.status === "error") setFailures(f => [...f, x]);
            else onSettled();
          }
        }
        setRuns(live);
        if (!live.length) return;   // nothing left to watch
      }
    } catch { /* a dropped sweep is not a failed run */ }
    timer.current = window.setTimeout(sweep, POLL_MS);
  }, [instance, onSettled]);

  // Pick up whatever is already running, including runs this tab never started.
  useEffect(() => {
    sweep();
    return () => window.clearTimeout(timer.current);
  }, [sweep]);

  const start = useCallback(async (student: string) => {
    setFailures(f => f.filter(x => x.student.toLowerCase() !== student.toLowerCase()));
    const optimistic: Run = {
      runId: `pending:${student}`, status: "running", student, stageLabel: "starting",
    };
    setRuns(r => [...r.filter(x => x.student !== student), optimistic]);
    try {
      const r = await fetch(`/api/${instance}/phase-review/generate`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ student }),
      });
      const d = await r.json() as { runId?: string; error?: string };
      if (!r.ok || !d.runId) throw new Error(d.error || `${r.status}`);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(sweep, POLL_MS);
    } catch (e: any) {
      setRuns(r => r.filter(x => x.runId !== optimistic.runId));
      setFailures(f => [...f, {
        runId: optimistic.runId, status: "error", student,
        error: String(e?.message || e),
      }]);
    }
  }, [instance, sweep]);

  const dismiss = useCallback((runId: string) => {
    setFailures(f => f.filter(x => x.runId !== runId));
  }, []);

  const isRunning = useCallback(
    (student: string) => runs.some(
      r => r.student.toLowerCase() === student.toLowerCase()),
    [runs]);

  return { runs, failures, start, dismiss, isRunning };
}

type Runs = ReturnType<typeof useRuns>;

function describe(r: Run): string {
  const bits = [r.stageLabel || "working"];
  if (typeof r.considerations === "number") {
    bits.unshift(`${r.considerations} considerations read`);
  }
  return bits.join(" · ");
}

function GenerateBar({ runs }: { runs: Runs }) {
  const [student, setStudent] = useState("");
  const busy = runs.isRunning(student.trim());

  return (
    <form
      className="pri-gen"
      onSubmit={e => {
        e.preventDefault();
        const name = student.trim();
        if (!name || busy) return;
        runs.start(name);
        setStudent("");
      }}
    >
      <input
        aria-label="Student name"
        className="pri-gen-input"
        onChange={e => setStudent(e.target.value)}
        placeholder="Student name"
        value={student}
      />
      <button className="pri-gen-go" disabled={!student.trim() || busy} type="submit">
        Generate
      </button>
    </form>
  );
}

/** What is running, and what just failed. Survives a refresh because the list
 *  comes from the server, not from this tab. */
function RunList({ runs }: { runs: Runs }) {
  if (!runs.runs.length && !runs.failures.length) return null;
  return (
    <ul className="pri-runs">
      {runs.runs.map(r => (
        <li className="pri-run" key={r.runId}>
          <span className="pri-run-spin" aria-hidden="true" />
          <span className="pri-run-name">{r.student}</span>
          <span className="pri-run-note">{describe(r)}</span>
        </li>
      ))}
      {runs.failures.map(r => (
        <li className="pri-run pri-run-failed" key={r.runId}>
          <span className="pri-run-name">{r.student}</span>
          <span className="pri-run-note">{r.error}</span>
          <button className="pri-run-x" onClick={() => runs.dismiss(r.runId)} type="button">
            Dismiss
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ============================================================
 *  the page
 * ============================================================ */

export function PhaseReviewIndex({ instance, me }: { instance: string; me: Me }) {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads(n => n + 1), []);

  useEffect(() => {
    let alive = true;
    fetch(`/api/${instance}/phase-review`)
      .then(async r => {
        if (r.status === 404) return { generatedAt: "", manualVersion: "", samples: [] };
        if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
        return r.json();
      })
      .then((d: Manifest) => alive && setManifest(d))
      .catch(e => alive && setErr(String(e.message || e)));
    return () => { alive = false; };
  }, [instance, reloads]);

  const samples = [...(manifest?.samples || [])].sort(byDepth);
  const counts = useMyMarkCounts(instance, samples, me.email || "");
  const runs = useRuns(instance, reload);

  if (err) return <div className="pr"><p>Could not load this page. {err}</p></div>;
  if (!manifest) return <div className="pr"><p className="pr-muted">Loading…</p></div>;

  return (
    <div className="pr">
      <div className="pr-head">
        <h1>Phase suggestion review</h1>
      </div>

      <GenerateBar runs={runs} />
      <RunList runs={runs} />

      {samples.length === 0
        ? <p className="pr-muted">Nothing published yet.</p>
        : (
          <div className="pri-grid">
            {samples.map(s => {
              const marks = counts[s.id] || 0;
              const regenerating = runs.isRunning(s.name);
              return (
                <div className="pri-card-wrap" key={s.id}>
                  <a
                    className="pri-card"
                    href={`/${instance}/phase-review/${encodeURIComponent(s.id)}?tab=inputs`}
                  >
                    <span className="pri-card-name">{s.name}</span>
                    <span className="pri-card-meta">
                      {s.considerations > 0
                        ? `${s.considerations} consideration${s.considerations === 1 ? "" : "s"}`
                        : "No notes"}
                    </span>
                    {marks > 0 && <span className="pri-card-marks">{marks} marked</span>}
                  </a>
                  <button
                    className="pri-card-again"
                    disabled={regenerating}
                    onClick={() => runs.start(s.name)}
                    title={`Design ${s.name} a new phase, replacing this one`}
                    type="button"
                  >
                    {regenerating ? "Redoing…" : "Redo"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
